import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { AlertTriangle, Info } from "lucide-react";

/** App-wide replacement for window.confirm/alert: a module-level store driven by one mounted host. */

let listener = null;        // the single host subscribes here
let queue = [];             // pending requests (one shown at a time)

function notify() {
  if (listener) listener(queue[0] || null);
}

function enqueue(req) {
  queue.push(req);
  notify();
}

function resolveTop(value) {
  const req = queue.shift();
  if (req) req.resolve(value);
  notify();
}

/** Ask the user to confirm; `altText` adds a third choice that resolves 'alt', leaving two-button callers untouched. */
export function confirm({
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  altText,
  tone = "default", // 'default' | 'destructive'
} = {}) {
  return new Promise((resolve) => {
    enqueue({
      mode: "confirm",
      title: title || (tone === "destructive" ? "Are you sure?" : "Please confirm"),
      description,
      confirmText,
      cancelText,
      altText,
      tone,
      resolve,
    });
  });
}

/** Show a message with a single dismiss button. Resolves when closed. */
export function alertDialog({
  title,
  description,
  confirmText = "OK",
  tone = "default", // 'default' | 'destructive'
} = {}) {
  return new Promise((resolve) => {
    enqueue({
      mode: "alert",
      title: title || (tone === "destructive" ? "Error" : "Notice"),
      description,
      confirmText,
      tone,
      resolve: () => resolve(undefined),
    });
  });
}

/** Ask for one short line of text. Resolves the string (possibly empty) on
 *  confirm, or NULL when dismissed — so a caller can tell "no reason given"
 *  from "changed my mind", which a bare '' cannot express. */
export function promptDialog({
  title,
  description,
  label,
  placeholder = "",
  initialValue = "",
  confirmText = "OK",
  cancelText = "Cancel",
  tone = "default", // 'default' | 'destructive'
} = {}) {
  return new Promise((resolve) => {
    enqueue({
      mode: "prompt",
      title: title || "",
      description,
      label,
      placeholder,
      initialValue,
      confirmText,
      cancelText,
      tone,
      resolve,
    });
  });
}

export function ConfirmDialogHost() {
  const [current, setCurrent] = React.useState(null);
  const [text, setText] = React.useState("");

  React.useEffect(() => {
    listener = setCurrent;
    return () => {
      listener = null;
    };
  }, []);

  // Each request opens on its OWN starting value, so a reason typed into one
  // dialog is never still sitting in the next one.
  React.useEffect(() => {
    if (current?.mode === "prompt") setText(current.initialValue || "");
  }, [current]);

  const open = !!current;
  const isDestructive = current?.tone === "destructive";
  const Icon = isDestructive ? AlertTriangle : Info;

  // Closing via overlay, Esc or X counts as cancel: false for a confirm, null
  // for a prompt (no answer given), nothing for a plain alert.
  const dismissValue = (mode) => {
    if (mode === "confirm") return false;
    if (mode === "prompt") return null;
    return undefined;
  };
  const handleOpenChange = (next) => {
    if (!next && current) resolveTop(dismissValue(current.mode));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span
              className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                isDestructive ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"
              }`}
            >
              <Icon className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              {current?.title && <DialogTitle>{current.title}</DialogTitle>}
              {current?.description && (
                <DialogDescription className="mt-1 whitespace-pre-line break-words">
                  {current.description}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>
        {current?.mode === "prompt" && (
          <div className="mt-1">
            {current.label && (
              <label htmlFor="prompt-dialog-field" className="text-sm text-muted-foreground">
                {current.label}
              </label>
            )}
            <Input
              id="prompt-dialog-field"
              className="mt-1"
              value={text}
              placeholder={current.placeholder}
              onChange={(e) => setText(e.target.value)}
              /* Enter submits: this dialog is one field, and reaching for the
                 mouse to confirm a single line is the slow way round. */
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); resolveTop(text); } }}
              autoFocus
            />
          </div>
        )}
        <DialogFooter className="mt-2 gap-2">
          {(current?.mode === "confirm" || current?.mode === "prompt") && (
            <Button
              type="button"
              variant="outline"
              onClick={() => resolveTop(dismissValue(current.mode))}
            >
              {current?.cancelText || "Cancel"}
            </Button>
          )}
          {/* The third way out sits between the two so reading order matches risk, and is deliberately not focused. */}
          {current?.mode === "confirm" && current?.altText && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => resolveTop("alt")}
            >
              {current.altText}
            </Button>
          )}
          <Button
            type="button"
            variant={isDestructive ? "destructive" : "default"}
            onClick={() => resolveTop(
              current?.mode === "confirm" ? true
                : current?.mode === "prompt" ? text
                  : undefined,
            )}
            /* The prompt's field takes the focus instead — landing on the
               button would mean tabbing backwards to type. */
            autoFocus={current?.mode !== "prompt"}
          >
            {current?.confirmText || "OK"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
